/**
 * Тексты правовых документов.
 *
 * Лежат данными, а не разметкой: показывает их один компонент, а править
 * формулировки можно, не трогая вёрстку. Ссылок на внешние страницы здесь нет
 * намеренно — документы должны открываться и без сети.
 *
 * Тексты описывают то, что игра действительно делает. Меняя обработку данных,
 * правьте и их: политика, которая расходится с кодом, хуже её отсутствия.
 */

import { operatorContact, operatorName } from "~/legal/operator";

export type LegalDocumentId = "terms" | "privacy" | "storage";

export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

export interface LegalDocument {
  id: LegalDocumentId;
  title: string;
  updated: string;
  sections: LegalSection[];
}

/** Дата последней правки текстов. Меняется вместе с ними. */
const UPDATED = "27 августа 2026 года";

const TERMS: LegalDocument = {
  id: "terms",
  title: "Условия использования",
  updated: UPDATED,
  sections: [
    {
      heading: "О чём договариваемся",
      paragraphs: [
        `Location King — игра, в которой по участку спутникового снимка нужно найти место на карте. Сервис ведёт ${operatorName()}, дальше — «мы».`,
        "Создавая учётную запись, вы соглашаетесь с этими условиями и с политикой конфиденциальности. Если что-то из написанного вам не подходит, учётную запись создавать не нужно.",
      ],
    },
    {
      heading: "Сколько это стоит",
      paragraphs: [
        "Нисколько. Игра бесплатна целиком, платных функций в ней нет, рекламу мы не показываем и данные игроков не продаём.",
        "Мы не обещаем, что сервис будет работать всегда. Он может быть недоступен из-за обновления, поломки или потому, что мы решили его закрыть. Предупредить заранее мы постараемся, но не обязаны.",
      ],
    },
    {
      heading: "Учётная запись",
      paragraphs: [
        "Одна учётная запись — один человек. Пароль храните при себе: всё, что сделано под вашей учётной записью, считается сделанным вами.",
        "Имя, которое вы указываете при регистрации, видно другим игрокам в таблице лидеров, в таблице челленджа дня и в комнатах. Не пишите там того, что не хотите показывать посторонним. Если имя не указать, мы подставим нейтральное вроде «Игрок A1B2».",
        "Учётную запись можно удалить в любой момент — кнопка есть в разделе «Профиль». Вместе с ней удаляются партии, раунды, созданные вами комнаты, оставленные отзывы и загруженная аватарка.",
      ],
    },
    {
      heading: "Аватарка",
      paragraphs: [
        "По умолчанию аватарка — узор, который рисует сама игра. Вместо него можно загрузить свою картинку: она видна другим игрокам там же, где имя, — в таблице лидеров, в челлендже дня и в комнатах.",
        "Загружая картинку, вы подтверждаете, что вправе это делать: что она ваша либо вы получили разрешение того, кому она принадлежит. Ответственность за загруженное несёт тот, кто его загрузил.",
      ],
      list: [
        "Не загружайте чужие фотографии без согласия изображённого человека.",
        "Не загружайте изображения, нарушающие закон, а также порнографию, насилие и разжигание вражды.",
        "Не выдавайте себя за другого человека — это относится и к аватарке, и к имени.",
      ],
    },
    {
      heading: "Как играть нельзя",
      list: [
        "Автоматизировать игру: боты, скрипты, подмена запросов к API.",
        "Пытаться получить координаты цели в обход правил игры.",
        "Мешать работе сервиса: нагрузочные атаки, перебор паролей, обход ограничений частоты запросов.",
        "Выдавать себя за другого человека в имени игрока или в аватарке.",
        "Массово выгружать содержимое сервиса для чужих сервисов.",
      ],
      paragraphs: [
        "За такое мы можем удалить загруженную аватарку, ограничить доступ или удалить учётную запись без предупреждения. Обычные проигрыши, неудачные догадки и долгие паузы к этому, разумеется, не относятся.",
      ],
    },
    {
      heading: "Чужие материалы",
      paragraphs: [
        "Спутниковые снимки и карта мира принадлежат не нам. Снимки приходят от стороннего провайдера, его название указано прямо на экране игры. Карта, на которой вы ставите точку, — OpenStreetMap, данные под лицензией ODbL.",
        "Игра показывает эти материалы для личного некоммерческого использования. Права на них остаются у правообладателей.",
      ],
    },
    {
      heading: "Ответственность",
      paragraphs: [
        "Сервис предоставляется «как есть». Мы не отвечаем за убытки, возникшие из-за его недоступности, ошибок в подсчёте очков или потери данных.",
        "Игра — развлечение. Не используйте её как источник географических или навигационных сведений.",
      ],
    },
    {
      heading: "Возраст",
      paragraphs: [
        "Сервис не предназначен для детей младше 14 лет. Если вам меньше, играть можно только с согласия родителей или другого законного представителя.",
      ],
    },
    {
      heading: "Изменения",
      paragraphs: [
        "Условия могут меняться. Действующая редакция всегда здесь, дата последней правки указана в начале. Продолжая играть после изменений, вы соглашаетесь с новой редакцией.",
        `Вопросы и претензии: ${operatorContact()}`,
      ],
    },
  ],
};

const PRIVACY: LegalDocument = {
  id: "privacy",
  title: "Политика конфиденциальности",
  updated: UPDATED,
  sections: [
    {
      heading: "Коротко",
      paragraphs: [
        "Мы собираем минимум: адрес почты, чтобы вы могли войти, и результаты партий, чтобы было что показать в таблице. Никакой аналитики, рекламных счётчиков и передачи данных третьим лицам. Всё удаляется одной кнопкой.",
      ],
    },
    {
      heading: "Кто обрабатывает данные",
      paragraphs: [
        `${operatorName()}. По любым вопросам об обработке данных пишите: ${operatorContact()}`,
      ],
    },
    {
      heading: "Что именно мы храним",
      list: [
        "Адрес электронной почты — он же логин.",
        "Пароль в виде необратимого хеша argon2id. Сам пароль мы не храним и не можем его узнать.",
        "Имя, которое вы выбрали для таблицы лидеров.",
        "Загруженную аватарку, если вы её загружали. Файл не сохраняется как есть: он перекодируется, и от него остаётся только изображение — без данных о съёмке, которые обычно несёт фотография с телефона.",
        "Ход игры: партии, раунды, догадки, расстояния, очки и время ответов.",
        "Даты создания учётной записи и последнего входа.",
        "Текст отзыва, если вы решили его написать, — вместе с тем, кто его оставил.",
        "IP-адрес — во временных счётчиках ограничения частоты запросов и в журналах веб-сервера.",
      ],
      paragraphs: [
        "Мы не запрашиваем настоящее имя, телефон, платёжные данные и не определяем ваше местоположение. Разрешение на геолокацию в браузере игра не просит.",
      ],
    },
    {
      heading: "Зачем",
      list: [
        "Почта и пароль — чтобы у вас была учётная запись и вы могли вернуться к своим партиям. Это исполнение договора с вами.",
        "Результаты партий — чтобы работали статистика, история, таблица лидеров, челлендж дня и комнаты. Ради этого вы и играете.",
        "IP-адрес — чтобы отличать людей от скриптов и защищаться от перебора паролей и наливания мусора в базу. Это наш законный интерес в безопасности сервиса.",
        "Отзыв — чтобы починить то, что сломано, и понять, куда игру вести дальше. Пишете вы его сами и только если захотите.",
      ],
      paragraphs: [
        "Писем мы не отправляем: почта нужна только для входа. Ни рассылок, ни уведомлений, ни передачи адреса кому-либо ещё.",
      ],
    },
    {
      heading: "Сколько это хранится",
      list: [
        "Учётная запись, результаты и отзывы — пока вы их не удалите.",
        "Счётчики ограничения частоты — не дольше часа, они стираются сами.",
        "Журналы веб-сервера — по настройке сервера, обычно несколько недель.",
      ],
    },
    {
      heading: "Кому мы это передаём",
      paragraphs: [
        "Никому. Аналитики, рекламных сетей и сторонних скриптов на странице нет вовсе — это проверяется строгой политикой безопасности содержимого.",
        "Одно исключение стоит знать: когда вы открываете карту мира, чтобы поставить точку, ваш браузер запрашивает фрагменты карты напрямую у серверов OpenStreetMap. Они видят ваш IP-адрес и версию браузера — как при заходе на любой сайт. Мы этих данных не получаем.",
        "Спутниковые снимки — наоборот: их забирает наш сервер и отдаёт вам сам. Провайдер снимков вашего адреса не видит.",
      ],
    },
    {
      heading: "Где данные лежат",
      paragraphs: [
        "На сервере, где развёрнута игра: база данных и кэш. Страну размещения определяет владелец сервиса — если это важно, спросите по контактному адресу выше.",
      ],
    },
    {
      heading: "Что вы можете сделать",
      list: [
        "Посмотреть, что мы о вас знаем, — всё это видно в разделах «Профиль» и «История».",
        "Изменить имя в таблице лидеров.",
        "Удалить учётную запись со всеми данными, включая отзывы, — кнопка «Удалить аккаунт» в разделе «Профиль». Удаление окончательное, восстановить нечего.",
        "Написать нам, если хотите получить копию данных, ограничить обработку или пожаловаться.",
      ],
      paragraphs: [
        "Если считаете, что мы обрабатываем ваши данные неправильно, вы вправе обратиться в надзорный орган по защите персональных данных в вашей стране.",
      ],
    },
    {
      heading: "Дети",
      paragraphs: [
        "Мы сознательно не собираем данные детей младше 14 лет. Если вы законный представитель и считаете, что ребёнок оставил здесь свои данные, напишите нам — мы удалим учётную запись.",
      ],
    },
    {
      heading: "Изменения",
      paragraphs: [
        "Политика может меняться вместе с игрой. Дата последней правки указана в начале документа.",
      ],
    },
  ],
};

const STORAGE: LegalDocument = {
  id: "storage",
  title: "Куки и хранилище браузера",
  updated: UPDATED,
  sections: [
    {
      heading: "Куки мы не ставим",
      paragraphs: [
        "Ни одного файла cookie игра не создаёт: ни своего, ни чужого. Рекламных и аналитических счётчиков на странице нет.",
      ],
    },
    {
      heading: "Что хранится в браузере",
      list: [
        "location-king:session — пара токенов доступа. Без неё вас выбрасывало бы из игры при каждом обновлении страницы.",
        "location-king:notice — отметка, что вы закрыли уведомление об этом хранилище, чтобы не показывать его снова.",
        "location-king:theme — выбранное оформление. Сам выбор хранится в вашей учётной записи, а копия в браузере нужна, чтобы страница открылась в нужной теме, не дожидаясь ответа сервера.",
        "location-king:menu — условия партии, режим и раздел, которые вы выбрали в меню. Лежат в браузере, чтобы не выставлять их заново после каждой игры.",
      ],
      paragraphs: [
        "Это localStorage, а не cookie: на сервер эти значения сами по себе не уходят, токен добавляется к запросу только когда игра обращается к API.",
      ],
    },
    {
      heading: "Почему нет окна «принять куки»",
      paragraphs: [
        "Согласие требуется на то, без чего сервис работает: аналитику, рекламу, профилирование. Ничего этого здесь нет, а хранилище используется строго для того, о чём вы попросили сами, — чтобы оставаться в игре под своей учётной записью.",
        "Поэтому вместо окна с кнопкой «принимаю», которое всё равно ничего не решает, мы просто рассказываем, что и зачем лежит в браузере.",
      ],
    },
    {
      heading: "Как всё стереть",
      paragraphs: [
        "Нажмите «Выйти» — токены удалятся. Полностью очистить хранилище можно в настройках браузера для этого сайта. Игра после этого продолжит работать, просто попросит войти заново.",
      ],
    },
    {
      heading: "Сторонние запросы",
      paragraphs: [
        "Карта мира, на которой вы ставите точку, подгружается с серверов OpenStreetMap — они видят ваш IP-адрес. Спутниковые снимки идут через наш сервер, и провайдер снимков вас не видит. Подробнее — в политике конфиденциальности.",
      ],
    },
  ],
};

export const LEGAL_DOCUMENTS: LegalDocument[] = [TERMS, PRIVACY, STORAGE];

export function legalDocument(id: LegalDocumentId): LegalDocument {
  const found = LEGAL_DOCUMENTS.find((document) => document.id === id);
  if (found === undefined) throw new Error(`Нет документа ${id}`);
  return found;
}
